import React, { useState, useEffect } from 'react';
import { 

  FileText, Sparkles, Shield, Database, Link2, 
  ChevronRight, Upload, TrendingUp, AlertTriangle,
  CheckCircle, Clock, Target, Zap, RefreshCw,
  ArrowRight, Info, X, Loader2, BarChart3,
  FileSearch, Brain, Activity, Users, AlertCircle
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Agent Card Component
const AgentCard = ({ agent, onSelect, isSelected }) => {
  const iconMap = {
    '📄': FileText,
    '🔮': Brain,
    '🛡️': Shield,
    '✨': Sparkles,
    '🔗': Link2
  };
  
  const IconComponent = iconMap[agent.icon] || FileText;
  
  return (
    <div 
      onClick={() => onSelect(agent)}
      className={`
        p-6 rounded-xl border-2 cursor-pointer transition-all duration-300
        ${isSelected 
          ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100' 
          : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md'
        }
      `}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center
          ${isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}
        `}>
          <IconComponent size={24} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 text-lg">{agent.name}</h3>
          <p className="text-sm text-slate-500 mt-1">{agent.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {agent.capabilities?.slice(0, 3).map((cap, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">
                {cap}
              </span>
            ))}
          </div>
        </div>
        <ChevronRight className={`text-slate-400 transition-transform ${isSelected ? 'rotate-90' : ''}`} size={20} />
      </div>
    </div>
  );
};

// Doc Decoder Panel
const DocDecoderPanel = () => {
  const [text, setText] = useState('');
  const [docType, setDocType] = useState('sustainability_report');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const extractData = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/advanced-agents/doc-decoder/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, document_type: docType })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Extraction error:', err);
    }
    setLoading(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <FileSearch className="text-blue-600" size={20} />
          Document Data Extraction
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Paste document text to extract ESG metrics automatically
        </p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Document Type</label>
          <select 
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            data-testid="doc-type-select"
          >
            <option value="sustainability_report">Sustainability Report</option>
            <option value="annual_report">Annual Report</option>
            <option value="supplier_questionnaire">Supplier Questionnaire</option>
            <option value="invoice">Utility Invoice</option>
            <option value="other">Other Document</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Document Text</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your document text here... Example: 'Our Scope 1 emissions for FY2024 were 12,500 tCO2e, a 5% reduction from the previous year...'"
            className="w-full h-40 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            data-testid="doc-text-input"
          />
        </div>
        
        <button
          onClick={extractData}
          disabled={loading || !text.trim()}
          className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="extract-btn"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : <Zap size={20} />}
          {loading ? 'Extracting...' : 'Extract ESG Data'}
        </button>
      </div>
      
      {result && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="extraction-result">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-900">Extraction Results</h4>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              result.quality_score >= 0.7 ? 'bg-green-100 text-green-700' : 
              result.quality_score >= 0.4 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
            }`}>
              Quality: {Math.round(result.quality_score * 100)}%
            </span>
          </div>
          
          {result.frameworks_detected?.length > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-2">Frameworks Detected</p>
              <div className="flex gap-2 flex-wrap">
                {result.frameworks_detected.map((fw, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {fw}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div>
            <p className="text-sm text-slate-500 mb-2">Extracted Metrics ({result.metrics_count})</p>
            <div className="space-y-2">
              {result.extracted_metrics?.map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-medium text-slate-900">{metric.metric_name.replace(/_/g, ' ').toUpperCase()}</span>
                    <p className="text-xs text-slate-500 mt-1">{metric.source_text}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-emerald-600">{metric.value.toLocaleString()} {metric.unit}</span>
                    <p className="text-xs text-slate-400">Confidence: {Math.round(metric.confidence * 100)}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Future Scout Panel
const FutureScoutPanel = () => {
  const [historicalData, setHistoricalData] = useState([
    { year: 2022, value: 15000 },
    { year: 2023, value: 14500 },
    { year: 2024, value: 14000 }
  ]);
  const [yearsAhead, setYearsAhead] = useState(5);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const forecast = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/advanced-agents/future-scout/forecast-emissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          historical_data: historicalData,
          years_ahead: yearsAhead
        })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Forecast error:', err);
    }
    setLoading(false);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="text-purple-600" size={20} />
          Emissions Forecasting
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Predict future emissions and assess alignment with targets
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">Historical Data (tCO2e)</label>
          <div className="grid grid-cols-3 gap-2">
            {historicalData.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="number"
                  value={item.year}
                  onChange={(e) => {
                    const updated = [...historicalData];
                    updated[idx].year = parseInt(e.target.value);
                    setHistoricalData(updated);
                  }}
                  className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Year"
                />
                <input
                  type="number"
                  value={item.value}
                  onChange={(e) => {
                    const updated = [...historicalData];
                    updated[idx].value = parseInt(e.target.value);
                    setHistoricalData(updated);
                  }}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Emissions"
                />
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Years to Forecast</label>
          <select
            value={yearsAhead}
            onChange={(e) => setYearsAhead(parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg"
          >
            <option value={3}>3 Years</option>
            <option value={5}>5 Years</option>
            <option value={10}>10 Years</option>
          </select>
        </div>
        
        <div className="flex items-end">
          <button
            onClick={forecast}
            disabled={loading}
            className="w-full py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="forecast-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Brain size={20} />}
            {loading ? 'Forecasting...' : 'Generate Forecast'}
          </button>
        </div>
      </div>
      
      {result && (
        <div className="space-y-4" data-testid="forecast-result">
          {/* Gap Analysis */}
          <div className={`p-6 rounded-xl border-2 ${
            result.gap_analysis?.on_track ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-slate-900">Target Alignment</h4>
                <p className="text-sm text-slate-600">{result.gap_analysis?.target_basis}</p>
              </div>
              <div className={`px-4 py-2 rounded-lg font-medium ${
                result.gap_analysis?.on_track ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
              }`}>
                {result.gap_analysis?.on_track ? '✓ On Track' : '⚠ Off Track'}
              </div>
            </div>
            {!result.gap_analysis?.on_track && (
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong className="text-red-600">Gap to Target:</strong> {result.gap_analysis?.gap?.toLocaleString()} tCO2e by 2030
                </p>
              </div>
            )}
          </div>
          
          {/* Scenarios */}
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(result.scenarios || {}).map(([name, scenario]) => (
              <div key={name} className="p-4 bg-white rounded-xl border border-slate-200">
                <h5 className="font-medium text-slate-900 capitalize">{name} Case</h5>
                <p className="text-xs text-slate-500 mt-1">{scenario.description}</p>
                <div className="mt-3 text-2xl font-bold text-slate-900">
                  {Object.values(scenario.predictions || {})[0]?.value?.toLocaleString() || '-'}
                  <span className="text-sm font-normal text-slate-500"> tCO2e</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Probability: {Math.round(scenario.probability * 100)}%
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Sentinel Panel
const SentinelPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkType, setCheckType] = useState('anomalies');
  
  const runCheck = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let body = {};
      
      if (checkType === 'anomalies') {
        endpoint = '/api/advanced-agents/sentinel/check-anomalies';
        body = {
          current_data: {
            scope1_emissions: 1500,
            scope2_emissions: 2200,
            energy_consumption: 5000
          },
          historical_baseline: {
            scope1_emissions: 1200,
            scope2_emissions: 2000,
            energy_consumption: 4500
          }
        };
      } else if (checkType === 'deadlines') {
        endpoint = '/api/advanced-agents/sentinel/check-deadlines';
        body = {
          deadlines: [
            { requirement: 'CSRD Report', regulation: 'CSRD', date: '2025-06-30' },
            { requirement: 'CDP Submission', regulation: 'CDP', date: '2025-07-31' },
            { requirement: 'SBTi Validation', regulation: 'SBTi', date: '2025-03-15' }
          ],
          warning_days: 60
        };
      }
      
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      setAlerts(data.alerts || []);
    } catch (err) {
      console.error('Check error:', err);
    }
    setLoading(false);
  };
  
  const getSeverityStyles = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      info: 'bg-slate-100 text-slate-800 border-slate-200'
    };
    return styles[severity] || styles.info;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Shield className="text-amber-600" size={20} />
          Real-time Monitoring
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Detect anomalies, threshold breaches, and upcoming deadlines
        </p>
      </div>
      
      <div className="flex gap-4">
        <select
          value={checkType}
          onChange={(e) => setCheckType(e.target.value)}
          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg"
          data-testid="check-type-select"
        >
          <option value="anomalies">Check Anomalies</option>
          <option value="deadlines">Check Deadlines</option>
        </select>
        
        <button
          onClick={runCheck}
          disabled={loading}
          className="px-6 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="run-check-btn"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : <Activity size={20} />}
          Run Check
        </button>
      </div>
      
      {alerts.length > 0 ? (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`p-4 rounded-xl border-2 ${getSeverityStyles(alert.severity)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5" size={20} />
                  <div>
                    <h5 className="font-medium">{alert.title}</h5>
                    <p className="text-sm opacity-80 mt-1">{alert.message}</p>
                    {alert.recommended_action && (
                      <p className="text-sm mt-2 opacity-70">
                        <strong>Action:</strong> {alert.recommended_action}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-xs font-medium uppercase px-2 py-1 rounded-full bg-white/50">
                  {alert.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : !loading && (
        <div className="text-center py-12 text-slate-500">
          <Shield size={48} className="mx-auto mb-4 opacity-50" />
          <p>No alerts found. Run a check to start monitoring.</p>
        </div>
      )}
    </div>
  );
};

// Data Curator Panel
const DataCuratorPanel = () => {
  const [rawData, setRawData] = useState(JSON.stringify([
    { scope1_emissions: "12,500 MT", energy_consumption: 5000, renewable_percentage: null },
    { scope1_emissions: 13000, energy_consumption: "4,800 kWh", renewable_percentage: 35 },
    { scope1_emissions: 12000, energy_consumption: 5200, renewable_percentage: 40 }
  ], null, 2));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const curateData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = JSON.parse(rawData);
      const response = await fetch(`${API}/api/advanced-agents/data-curator/curate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data })
      });
      const curated = await response.json();
      setResult(curated);
    } catch (err) {
      console.error('Curation error:', err);
    }
    setLoading(false);
  };
  
  const getQualityColor = (level) => {
    const colors = {
      excellent: 'text-green-600 bg-green-100',
      good: 'text-emerald-600 bg-emerald-100',
      fair: 'text-yellow-600 bg-yellow-100',
      poor: 'text-orange-600 bg-orange-100',
      critical: 'text-red-600 bg-red-100'
    };
    return colors[level] || colors.fair;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-6 border border-cyan-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="text-cyan-600" size={20} />
          Data Quality & Enrichment
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Clean, standardize, and validate your ESG data
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Raw Data (JSON Array)</label>
        <textarea
          value={rawData}
          onChange={(e) => setRawData(e.target.value)}
          className="w-full h-48 px-4 py-3 border border-slate-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-cyan-500"
          data-testid="raw-data-input"
        />
      </div>
      
      <button
        onClick={curateData}
        disabled={loading}
        className="w-full py-3 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="curate-btn"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
        {loading ? 'Curating...' : 'Curate Data'}
      </button>
      
      {result && (
        <div className="space-y-4" data-testid="curation-result">
          <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
            <div>
              <h4 className="font-semibold text-slate-900">Curation Complete</h4>
              <p className="text-sm text-slate-500">
                {result.records_processed} records processed, {result.transformations_applied} transformations
              </p>
            </div>
            <div className={`px-4 py-2 rounded-lg font-medium ${getQualityColor(result.quality_level)}`}>
              {result.quality_level?.toUpperCase()} ({Math.round(result.quality_score * 100)}%)
            </div>
          </div>
          
          {result.quality_issues?.length > 0 && (
            <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <h5 className="font-medium text-yellow-800 flex items-center gap-2">
                <AlertCircle size={18} />
                Quality Issues Found ({result.quality_issues.length})
              </h5>
              <ul className="mt-2 space-y-1">
                {result.quality_issues.slice(0, 5).map((issue, idx) => (
                  <li key={idx} className="text-sm text-yellow-700">
                    • Record {issue.record_index}: {issue.field} - {issue.issue}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Supply Chain Analyst Panel
const SupplyChainPanel = () => {
  const [supplierData, setSupplierData] = useState({
    supplier_id: 'SUP-001',
    name: 'Acme Components Ltd',
    country: 'CN',
    industry: 'manufacturing',
    esg_score: 45,
    annual_spend: 500000
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const assessSupplier = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/advanced-agents/supply-chain/assess-supplier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ supplier_data: supplierData })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Assessment error:', err);
    }
    setLoading(false);
  };
  
  const getRiskColor = (level) => {
    const colors = {
      critical: 'text-red-600 bg-red-100 border-red-200',
      high: 'text-orange-600 bg-orange-100 border-orange-200',
      medium: 'text-yellow-600 bg-yellow-100 border-yellow-200',
      low: 'text-green-600 bg-green-100 border-green-200'
    };
    return colors[level] || colors.medium;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Link2 className="text-violet-600" size={20} />
          Supplier ESG Assessment
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Assess supplier ESG risks and engagement priorities
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Supplier Name</label>
          <input
            type="text"
            value={supplierData.name}
            onChange={(e) => setSupplierData({...supplierData, name: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
          <select
            value={supplierData.country}
            onChange={(e) => setSupplierData({...supplierData, country: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          >
            <option value="US">United States</option>
            <option value="DE">Germany</option>
            <option value="CN">China</option>
            <option value="IN">India</option>
            <option value="VN">Vietnam</option>
            <option value="BD">Bangladesh</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
          <select
            value={supplierData.industry}
            onChange={(e) => setSupplierData({...supplierData, industry: e.target.value})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          >
            <option value="manufacturing">Manufacturing</option>
            <option value="electronics">Electronics</option>
            <option value="textiles">Textiles</option>
            <option value="chemicals">Chemicals</option>
            <option value="services">Services</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ESG Score (0-100)</label>
          <input
            type="number"
            value={supplierData.esg_score}
            onChange={(e) => setSupplierData({...supplierData, esg_score: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            min="0"
            max="100"
          />
        </div>
      </div>
      
      <button
        onClick={assessSupplier}
        disabled={loading}
        className="w-full py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="assess-supplier-btn"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Users size={20} />}
        {loading ? 'Assessing...' : 'Assess Supplier'}
      </button>
      
      {result && (
        <div className="space-y-4" data-testid="assessment-result">
          <div className={`p-6 rounded-xl border-2 ${getRiskColor(result.risk_level)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-lg">{result.supplier_name}</h4>
                <p className="text-sm opacity-80">{result.engagement_action}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">{result.overall_risk_score}</div>
                <div className="text-sm font-medium uppercase">{result.risk_level} Risk</div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(result.risk_factors || {}).map(([factor, data]) => (
              <div key={factor} className="p-4 bg-white rounded-xl border border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600 capitalize">{factor.replace('_', ' ')}</span>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${getRiskColor(data.level)}`}>
                    {data.score}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{data.reason}</p>
              </div>
            ))}
          </div>
          
          {result.recommendations?.length > 0 && (
            <div className="p-4 bg-violet-50 rounded-xl border border-violet-200">
              <h5 className="font-medium text-violet-800 mb-2">Recommendations</h5>
              <ul className="space-y-2">
                {result.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-violet-700">
                    <ArrowRight size={16} className="mt-0.5 flex-shrink-0" />
                    <span><strong>{rec.priority}:</strong> {rec.action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const AdvancedAIAgents = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAgentCapabilities();
  }, []);
  
  const fetchAgentCapabilities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/advanced-agents/capabilities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAgents(data.agents || []);
      if (data.agents?.length > 0) {
        setSelectedAgent(data.agents[0]);
      }
    } catch (err) {
      console.error('Error fetching capabilities:', err);
      // Set default agents if API fails
      setAgents([
        { id: 'doc_decoder', name: 'Doc Decoder', icon: '📄', description: 'Extract ESG data from documents', capabilities: ['PDF extraction', 'Invoice parsing', 'Report analysis'] },
        { id: 'future_scout', name: 'Future Scout', icon: '🔮', description: 'Predictive analytics', capabilities: ['Emissions forecasting', 'Risk prediction', 'Scenario modeling'] },
        { id: 'sentinel', name: 'Sentinel', icon: '🛡️', description: 'Real-time monitoring', capabilities: ['Anomaly detection', 'Threshold alerts', 'Deadline tracking'] },
        { id: 'data_curator', name: 'Data Curator', icon: '✨', description: 'Data quality', capabilities: ['Auto-correction', 'Imputation', 'Standardization'] },
        { id: 'supply_chain_analyst', name: 'Supply Chain Analyst', icon: '🔗', description: 'Supplier intelligence', capabilities: ['Risk scoring', 'Scope 3 mapping', 'Engagement'] }
      ]);
      setSelectedAgent({ id: 'doc_decoder', name: 'Doc Decoder', icon: '📄', description: 'Extract ESG data from documents' });
    }
    setLoading(false);
  };
  
  const renderAgentPanel = () => {
    if (!selectedAgent) return null;
    
    switch (selectedAgent.id) {
      case 'doc_decoder':
        return <DocDecoderPanel />;
      case 'future_scout':
        return <FutureScoutPanel />;
      case 'sentinel':
        return <SentinelPanel />;
      case 'data_curator':
        return <DataCuratorPanel />;
      case 'supply_chain_analyst':
        return <SupplyChainPanel />;
      default:
        return <div className="text-center py-12 text-slate-500">Select an agent to get started</div>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-emerald-600" size={48} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="advanced-ai-agents">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Brain size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Advanced AI Agents</h1>
            <p className="text-emerald-100 mt-1">
              Specialized AI capabilities for document intelligence, forecasting, monitoring, and more
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Agent Selection */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Select Agent</h2>
          {agents.map(agent => (
            <AgentCard 
              key={agent.id}
              agent={agent}
              isSelected={selectedAgent?.id === agent.id}
              onSelect={setSelectedAgent}
            />
          ))}
        </div>
        
        {/* Agent Panel */}
        <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          {renderAgentPanel()}
        </div>
      </div>
    </div>
  );
};

export default AdvancedAIAgents;
